import cx from "classnames";
import React from "react";
import { connect } from "react-redux";

import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import {
    advancedArticleUrl,
    blogsArticleUrl,
    blogsPostDetailUrl,
    blogsUrl,
} from "@/mk2/apps/blogs/urls";
import {
    PostEntity,
    PostSchema,
    ProductTestingEntity,
    ProductTestingSchema,
} from "@/mk2/apps/forum/schemas";
import ReviewBlogPostPreview from "@/mk2/apps/wiki/components/ReviewBlogPostPreview";
import { Link } from "@/mk2/components/Link";
import {
    getAllDescendantsOfAType,
    getAttributesDict,
    getFirstParagraphsPlaintext,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { url } from "@/mk2/helpers/urls";
import { AppState } from "@/mk2/reducers";
import {
    AST,
    ASTAdditionalDataFromServer,
    ASTLinkedObjectType,
    BlogPostEntity,
    BlogPostSchema,
    BlogPostType,
    StrollerBrandEntity,
    StrollerBrandSchema,
    StrollerEntity,
    StrollerSchema,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { findDenormalizedEntityByCond, getDenormalizedEntity } from "@/mk2/selectors";
import {
    WIKI_REVIEW_TESTINGS_HEADER,
    WIKI_REVIEW_TESTINGS_MORE,
} from "@/mk/autogenerated/translation";

import styles from "./ReviewTestings.mscss";

interface OwnProps {
    className?: string;
    testingAST: AST;
    testingASTAdditionalData: ASTAdditionalDataFromServer;
}

interface StateProps {
    testing: ProductTestingEntity;
    testingPosts: LinkedObjectEntityWithType[];
}

type Props = OwnProps & StateProps;

class ReviewTestings extends React.PureComponent<Props> {
    public render() {
        const { className, testing, testingPosts } = this.props;
        return testing && testingPosts ? (
            <div className={cx(styles.ReviewTestings, className)}>
                <h2 className={styles.ReviewTestings__heading}>{WIKI_REVIEW_TESTINGS_HEADER}</h2>
                {testingPosts.map((testingPost, idx) => {
                    let author = null;
                    let photo = null;
                    let title = null;
                    let postUrl = null;
                    if (testingPost.type === ASTLinkedObjectType.PHOTOBLOG_ARTICLE) {
                        const blogArticle = testingPost.entity as BlogPostEntity;
                        author = blogArticle.user;
                        photo = blogArticle.photos[0];
                        title = getArticleTitleAsString(blogArticle.titleAST);
                        postUrl = advancedArticleUrl(blogArticle);
                    } else if (
                        testingPost.type === ASTLinkedObjectType.PHOTOBLOG_MESSAGE ||
                        testingPost.type === ASTLinkedObjectType.PHOTOBLOG_ALBUM
                    ) {
                        const blogPost = testingPost.entity as BlogPostEntity;
                        author = blogPost.author;
                        photo = blogPost.images && blogPost.images[0]?.photo;
                        title = getFirstParagraphsPlaintext(blogPost.perexAST);
                        postUrl = url(blogsPostDetailUrl(blogPost.type), {
                            username: author.username,
                            postSlug: blogPost.slug,
                        });
                    }

                    return author && photo && title && postUrl ? (
                        <ReviewBlogPostPreview
                            className={styles.ReviewTestings__item}
                            key={idx}
                            author={author}
                            photo={photo}
                            title={title}
                            postUrl={postUrl}
                        />
                    ) : null;
                })}
                <Link
                    to={url(
                        blogsUrl,
                        {},
                        { h: testing.trackedHashtag, post_type: BlogPostType.ARTICLE },
                    )}
                    className={styles.ReviewTestings__more}
                >
                    {WIKI_REVIEW_TESTINGS_MORE}
                </Link>
            </div>
        ) : null;
    }
}

declare type LinkedObjectEntity =
    | PostEntity
    | WikiArticleEntity
    | BlogPostEntity
    | StrollerEntity
    | StrollerBrandEntity;

interface LinkedObjectEntityWithType {
    entity: LinkedObjectEntity;
    type: ASTLinkedObjectType;
}

function getDenormalizedEntityForLinkedObject(
    state: AppState,
    objType: ASTLinkedObjectType,
    objId: number,
): LinkedObjectEntityWithType {
    if (objType === ASTLinkedObjectType.FORUM_POST_DETAIL) {
        const objEntity = getDenormalizedEntity<PostEntity>(state, PostSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.WIKI_ARTICLE) {
        const objEntity = getDenormalizedEntity<WikiArticleEntity>(state, WikiArticleSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.PHOTOBLOG_ARTICLE) {
        const objEntity = getDenormalizedEntity<BlogPostEntity>(state, BlogPostSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.PHOTOBLOG_MESSAGE) {
        const objEntity = getDenormalizedEntity<BlogPostEntity>(state, BlogPostSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.PHOTOBLOG_ALBUM) {
        const objEntity = getDenormalizedEntity<BlogPostEntity>(state, BlogPostSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.STROLLERS_STROLLER_DETAIL) {
        const objEntity = getDenormalizedEntity<StrollerEntity>(state, StrollerSchema, objId);
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    if (objType === ASTLinkedObjectType.STROLLERS_STROLLER_BRAND) {
        const objEntity = getDenormalizedEntity<StrollerBrandEntity>(
            state,
            StrollerBrandSchema,
            objId,
        );
        return objEntity ? { type: objType, entity: objEntity } : null;
    }
    return null;
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { testingAST, testingASTAdditionalData } = ownProps;
    let testing = null;
    const testingPosts = [];
    if (testingAST && testingASTAdditionalData.linkedObjects) {
        const linkToObjectNodes = getAllDescendantsOfAType(testingAST, "link_to_object");
        for (const linkToObjectNode of linkToObjectNodes) {
            const objType: ASTLinkedObjectType = linkToObjectNode.obj_type;
            const objId = linkToObjectNode.obj_id;
            const testingPost = getDenormalizedEntityForLinkedObject(state, objType, objId);
            if (testingPost) {
                testingPosts.push(testingPost);
            }
        }

        const testingAttributes = getAttributesDict(testingAST);
        const testingHashtag = testingAttributes.hasOwnProperty("hashtag")
            ? removeQuotes(testingAttributes.hashtag)
            : null;
        if (testingHashtag) {
            testing = findDenormalizedEntityByCond<ProductTestingEntity>(
                state,
                ProductTestingSchema,
                `cacheKey-ReviewTestingComponent-${testingHashtag}-productTesting`,
                (sel) =>
                    sel.trackedHashtag ===
                    `${testingHashtag.startsWith("test_") ? "" : "test_"}${testingHashtag}`,
            );
        }
    }

    return {
        testing,
        testingPosts,
    };
}

export default connect(mapStateToProps)(ReviewTestings);
